import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { UserCog, Mail, Shield } from "lucide-react";

export default function Officers() {
  const [users, setUsers] = useState([]);
  const [villages, setVillages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [selected, setSelected] = useState([]);

  const load = () => {
    setLoading(true);
    Promise.all([api.get("/users"), api.get("/villages")])
      .then(([u, v]) => { setUsers(u.data); setVillages(v.data); })
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const openAssign = (u) => {
    setEditing(u);
    setSelected(u.assigned_villages || []);
  };

  const toggle = (name) => {
    setSelected((p) => p.includes(name) ? p.filter((x) => x !== name) : [...p, name]);
  };

  const save = async () => {
    try {
      await api.patch(`/users/${editing.user_id}/villages`, { village_names: selected });
      toast.success(`Assigned ${selected.length} village(s) to ${editing.name}`);
      setEditing(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  return (
    <div className="p-6 lg:p-8" data-testid="officers-page">
      <div className="mb-6">
        <h1 className="font-heading font-bold text-3xl">Officers & Assignments</h1>
        <p className="text-gray-600 text-sm">Assign officers to villages for door-to-door census collection.</p>
      </div>

      <div className="bg-white rounded-2xl border border-gray-100 shadow-[0_2px_10px_rgba(0,0,0,0.04)] overflow-hidden">
        {loading ? (
          <div className="p-10 text-gray-500 text-center">Loading…</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-left">
              <tr className="text-xs uppercase tracking-wider text-gray-600">
                <th className="p-3">Name</th>
                <th className="p-3">Email</th>
                <th className="p-3">Role</th>
                <th className="p-3">Auth</th>
                <th className="p-3">Assigned Villages</th>
                <th className="p-3"></th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => (
                <tr key={u.user_id} className="border-t border-gray-100" data-testid={`officer-row-${u.email}`}>
                  <td className="p-3">
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 rounded-full bg-[#FEF3C7] flex items-center justify-center text-[#92400E] font-bold">
                        {u.name?.[0]?.toUpperCase()}
                      </div>
                      <div className="font-medium">{u.name}</div>
                    </div>
                  </td>
                  <td className="p-3 text-gray-600"><span className="inline-flex items-center gap-1"><Mail className="w-3 h-3" /> {u.email}</span></td>
                  <td className="p-3">
                    <Badge className={u.role === "admin" ? "bg-[#E11D48] hover:bg-[#E11D48]" : "bg-[#1E3A8A] hover:bg-[#1E3A8A]"}>
                      <Shield className="w-3 h-3 mr-1" /> {u.role}
                    </Badge>
                  </td>
                  <td className="p-3 text-xs text-gray-500">{u.auth_provider}</td>
                  <td className="p-3">
                    <div className="flex flex-wrap gap-1">
                      {(u.assigned_villages || []).slice(0, 3).map((v) => (
                        <Badge key={v} variant="outline">{v}</Badge>
                      ))}
                      {(u.assigned_villages || []).length > 3 && (
                        <Badge variant="outline">+{u.assigned_villages.length - 3} more</Badge>
                      )}
                      {(u.assigned_villages || []).length === 0 && <span className="text-xs text-gray-400">None</span>}
                    </div>
                  </td>
                  <td className="p-3">
                    {u.role === "officer" && (
                      <Button size="sm" variant="outline" onClick={() => openAssign(u)} data-testid={`assign-${u.email}`}>
                        <UserCog className="w-4 h-4 mr-1" /> Assign
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <Dialog open={!!editing} onOpenChange={(v) => !v && setEditing(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="font-heading">Assign villages to {editing?.name}</DialogTitle>
          </DialogHeader>
          <div className="grid sm:grid-cols-3 gap-2 max-h-96 overflow-y-auto">
            {villages.map((v) => (
              <label key={v.village_id} className={`flex items-center gap-2 p-2.5 border rounded-md cursor-pointer ${selected.includes(v.name) ? "border-[#1E3A8A] bg-[#1E3A8A]/5" : "border-gray-200"}`}>
                <Checkbox checked={selected.includes(v.name)} onCheckedChange={() => toggle(v.name)} data-testid={`assign-village-${v.name}`} />
                <span className="text-sm">{v.name}</span>
              </label>
            ))}
          </div>
          <div className="text-xs text-gray-500">{selected.length} village(s) selected</div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)}>Cancel</Button>
            <Button onClick={save} className="bg-[#1E3A8A] hover:bg-[#1E3A8A]/90" data-testid="save-assignment">Save assignment</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
